import React, {
  FC,
  useEffect,
  useState,
  useCallback,
  memo,
  useRef,
} from "react";
import "sortable-table-pattern/dist/main.css";
import Dropdown, { DropdownOption } from "./Dropdown";
import CustomDatepicker from "./DatePicker/Datepicker";

export interface EditableTableColumn {
  key: string;
  label: string;
  className?: string;
  isSortable?: boolean;
  isEdit?: boolean;
  disabled?: boolean;
  editType?: "text" | "dropdown" | "date";
  options?: DropdownOption[];
  error?: string;
}

export interface EditableTableRow {
  id?: string | number;
  rowKey?: string;
  [key: string]: React.ReactNode;
}

export interface EditableTableProps {
  columns: EditableTableColumn[];
  rows: EditableTableRow[];
  isEditMode?: boolean;
  onRowsChange?: (rows: EditableTableRow[]) => void;
  onCellBlur?: (rows: EditableTableRow[]) => void;
  onActionClick?: (rowData: EditableTableRow) => void;
  actionKey?: string;
  caption?: string;
  className?: string;
}

const EditableTable: FC<EditableTableProps> = ({
  columns,
  rows,
  isEditMode = false,
  onRowsChange,
  onCellBlur,
  onActionClick,
  actionKey,
  caption,
  className,
}) => {
  const [localRows, setLocalRows] = useState<EditableTableRow[]>(rows);
  // Keep event handlers on the latest edit so rapid input cannot submit a stale row.
  const latestRowsRef = useRef<EditableTableRow[]>(rows);
  const updateInProgressRef = useRef(false);

  // Sync from props, but skip if we just initiated an update from within this component
  // This prevents the "reset" issue during typing
  useEffect(() => {
    if (updateInProgressRef.current) {
      updateInProgressRef.current = false;
      return;
    }
    latestRowsRef.current = rows;
    setLocalRows(rows);
  }, [rows]);

  const updateCell = useCallback(
    (rowIndex: number, key: string, value: string) => {
      // Flag that this update originated from user input in this component
      updateInProgressRef.current = true;

      const next = latestRowsRef.current.map((row, index) =>
        index === rowIndex ? { ...row, [key]: value } : row,
      );
      latestRowsRef.current = next;
      setLocalRows(next);
      // Notify consumers after scheduling local state; parent updates must not run
      // inside React's state-updater callback.
      onRowsChange?.(next);
      
      // Reset flag after a tick to allow subsequent external updates
      setTimeout(() => {
        updateInProgressRef.current = false;
      }, 0);
    },
    [onRowsChange],
  );

  const renderDropdownCell = useCallback(
    (col: EditableTableColumn, row: EditableTableRow, rowIndex: number) => {
      const value = typeof row[col.key] === 'string' ? row[col.key] as string : "";
      return (
        <Dropdown
          menuClassName="min-w-40 w-full!"
          itemClassName="pr-4!"
          buttonClassName="w-full!"
          options={col.options!}
          selectedValue={
            col.options!.find((opt) => opt.value === value) ?? col.options![0]
          }
          onSelect={(option) =>
            updateCell(rowIndex, col.key, option ? option.value : "")
          }
          placeholder=""
        />
      );
    },
    [updateCell],
  );

  const renderDateCell = useCallback(
    (col: EditableTableColumn, row: EditableTableRow, rowIndex: number) => {
      const value = typeof row[col.key] === 'string' ? row[col.key] as string : "";
      return (
        <CustomDatepicker
          name={col.key}
          value={value}
          onChange={(_, dateValue) => updateCell(rowIndex, col.key, dateValue)}
          onValueBlur={(_, dateValue) => {
            // Date consumers may need to validate or persist only after blur.
            const next = latestRowsRef.current.map((currentRow, index) =>
              index === rowIndex ? { ...currentRow, [col.key]: dateValue } : currentRow,
            );
            latestRowsRef.current = next;
            onCellBlur?.(next);
          }}
          error={col.error}
          disabled={col.disabled}
        />
      );
    },
    [onCellBlur, updateCell],
  );

  const renderTextInputCell = useCallback(
    (col: EditableTableColumn, row: EditableTableRow, rowIndex: number) => {
      return (
        <div
          className="ds-form w-full!"
          data-ds-theme="vessel"
          data-ds-variant="input"
        >
          <input
            id={String(rowIndex)}
            type="text"
            value={(row[col.key] as string) ?? ""}
            onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
            disabled={col.disabled}
            className={`ds-text-input ${col.disabled ? 'ds-form__disabled-field bg-gray-100! text-gray-600!' : ''} ${col.error ? 'border-border-red! focus:outline-border-red!' : ''}`}
          />
        </div>
      );
    },
    [updateCell],
  );

  const renderActionCell = useCallback(
    (row: EditableTableRow) => {
      return (
        <button
          type="button"
          className={`-medium text-kp-blue-dark! text-left underline cursor-pointer bg-transparent border-0 p-0`}
          data-analytics-type="link"
          data-analytics-click="General click"
          onClick={(e) => {
            e.preventDefault();
            onActionClick!(row);
          }}
        >
          {row[actionKey!]}
        </button>
      );
    },
    [onActionClick, actionKey],
  );

  const renderCellContent = useCallback(
    (col: EditableTableColumn, row: EditableTableRow, rowIndex: number) => {
      if (isEditMode && col.isEdit) {
        if (col.editType === "dropdown" && Array.isArray(col.options)) {
          return renderDropdownCell(col, row, rowIndex);
        }
        if (col.editType === "date") {
          return renderDateCell(col, row, rowIndex);
        }
        return renderTextInputCell(col, row, rowIndex);
      }

      if (col.key === actionKey && onActionClick) {
        return renderActionCell(row);
      }

      return row[col.key];
    },
    [isEditMode, actionKey, onActionClick, renderDropdownCell, renderDateCell, renderTextInputCell, renderActionCell],
  );

  return (
    <div className="flex flex-col w-full">
      <table
        className={`ds-data-table ds-data-table--stripes-odd ${className || ""}`}
        data-ds-theme="vessel"
        data-ds-variant="basic"
        data-ds-version="1"
      >
        {caption && (
          <caption>
            <p className="--medium">{caption}</p>
          </caption>
        )}
        <thead>
          <tr>
            {columns.map((col) => {
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={col.className}
                >
                  {col.label}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {localRows.map((row, rowIndex) => {
            const rowKey = String(row.rowKey ?? row.id ?? rowIndex);
            return (
              <tr key={rowKey}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`text-[14px]! ${col.className || ""}`}
                  >
                    <span className="table-head">{col.label}</span>
                    <span className="table-text">
                      {renderCellContent(col, row, rowIndex)}
                    </span>
                    {col.error && col.editType !== 'date' && (
                      <div className="error-description mt-1">
                        <ul>
                          <li className="flex items-start gap-1">
                            <img
                              src="/assets/images/errorCircleSmall.svg"
                              alt="Error icon"
                              width="14"
                              height="14"
                              className="mt-0.5"
                            />
                            <span className="text-red-600 text-[12px]">{col.error}</span>
                          </li>
                        </ul>
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default memo(EditableTable);
 